import React, { useState, useEffect } from "react";
import { Button, Card, CardBody, Row, Col, Container, Badge } from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import { Triangle } from "react-loader-spinner";
import toast from "react-hot-toast";

import DaybookService from "../../services/OrderingServices/DaybookService";
import CustomDataTable from "../../Components/CustomTable/CustomTable";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import StatusBadge from "../../Components/Common/Badge";
import SettingService from "../../services/OrderingServices/SettingService";
import CloseSummaryPrint from "../../OrderingSystem/Dashboard/components/CardComponent/CloseSummaryPrint";
import ConfirmationDaybook from "../../OrderingSystem/Dashboard/components/CardComponent/ConfirmationDaybook";
import NumberWithCommas from "../../pages/Pages/Starter/NumberWithCommas";
import { formatDateTimes } from "../../Components/Common/FormatDate";

const customStyles = {
  headCells: {
    style: {
      fontSize: "0.92rem",
      fontWeight: 610,
    },
  },
  rows: {
    style: {
      fontSize: "0.9rem",
    },
  },
};

const StatusBadgeOld = ({ status, type }) => {
  let cls = "bg-secondary";
  if (type === "order") {
    cls =
      status?.toLowerCase() === "ordered"
        ? "bg-warning"
        : status?.toLowerCase() === "preparing"
        ? "bg-secondary"
        : status?.toLowerCase() === "ready"
        ? "bg-primary"
        : status?.toLowerCase() === "completed"
        ? "bg-success"
        : status?.toLowerCase() === "cancelled"
        ? "bg-danger"
        : "bg-secondary";
  } else if (type === "payment") {
    cls =
      status?.toLowerCase() === "pending"
        ? "bg-warning"
        : status?.toLowerCase() === "paid"
        ? "bg-success"
        : status?.toLowerCase() === "failed"
        ? "bg-danger"
        : "bg-light";
  }
  return <span className={`badge ${cls}`}>{status}</span>;
};

const SummaryCard = ({ title, value, color, icon }) => (
  <Card className="shadow-sm card-animate border-0">
    <CardBody className="d-flex align-items-center justify-content-between">
      <div className="w-100">
        <h6 className="fw-semibold text-uppercase" style={{ color }}>
          {title}
        </h6>
        <div className="d-flex justify-content-between w-100">
          <h4 className="fs-2 fw-bold" style={{ color }}>
            <NumberWithCommas number={value} />
          </h4>
          <div
            className="rounded-circle d-flex align-items-center justify-content-center"
            style={{
              backgroundColor: color + "20",
              color,
              width: "36px",
              height: "36px",
            }}
          >
            <i className={`${icon} fs-5`} />
          </div>
        </div>
      </div>
    </CardBody>
  </Card>
);

const DaybookOrderSummary = () => {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [summary, setSummary] = useState([]);
  const [isPrinting, setIsPrinting] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [isDayOpen, setIsDayOpen] = useState(null);
  const [isDayClosed, setIsDayClosed] = useState(false);
  const [orderData, setOrderData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Summary stats
  const [totalOrders, setTotalOrders] = useState(0);
  const [totalSalesAmount, setTotalSalesAmount] = useState(0);
  const [actualSalesAmount, setActualSalesAmount] = useState(0);
  const [ongoingOrderValue, setOngoingOrderValue] = useState(0);

  const [responseModal, setResponseModal] = useState(false);
  const [responseData, setResponseData] = useState([]);

  const toggleResponse = () => setResponseModal(!responseModal);
  const toggleConfirm = () => setShowCloseModal(!showCloseModal);

  // Get current date
  const getCurrentDate = () => {
    const today = new Date();
    return today.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };
  const getStatusBadge = (status) => (
    <StatusBadge status={status} type="order" pill={true} />
  );

  const getPaymentBadge = (status) => (
    <StatusBadge status={status} type="payment" pill={true} />
  );

  // Fetch today's summary
  const fetchTodayData = async () => {
    setIsLoading(true);
    try {
      const [paymentResult, orderResult, settingResult] =
        await Promise.allSettled([
          DaybookService.paymentTypeSummary(),
          DaybookService.orderSummary(),
          SettingService.getDaySetting(),
        ]);

      if (orderResult.status === "fulfilled") {
        const orders = orderResult.value || [];
        setSummary(orders.items || []);
        setOrderData(orders.items || []);

        setTotalOrders(orders?.summary?.totalOrders);
        setTotalSalesAmount(orders?.summary?.totalSalesAmount);
        setActualSalesAmount(orders?.summary?.actualSalesAmount);
        setOngoingOrderValue(orders?.summary?.ongoingOrderValue);
      }

      if (settingResult.status === "fulfilled") {
        setIsDayOpen(settingResult.value.isActive);
        setIsDayClosed(!settingResult.value.isActive);
      }
    } catch (err) {
      toast.error("Failed to fetch daybook data");
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTodayData();
  }, []);

  // Toggle Day Open/Close
  const handleDayToggle = async () => {
    const newStatus = !isDayOpen;

    if (!newStatus) {
      setShowCloseModal(true);
      return;
    }

    await performDayToggle(newStatus);
  };

  const performDayToggle = async (newStatus) => {
    setIsDayOpen(newStatus);
    setLoading(true);
    try {
      if (!newStatus) {
        const response = await DaybookService.closeSummary();
        console.log("🚀 ~ performDayToggle ~ response:", response);
        setResponseData(response);
        toggleResponse();
        toast.success("Day is closed!");
      } else {
        const response = await SettingService.setOpenDay();
        toast.success(response);
      }
    } catch (error) {
      toast.error(error?.response?.data);
      setIsDayOpen(!newStatus);
    } finally {
      setLoading(false);
    }
  };

  // Print
  const handlePrint = () => {
    setIsPrinting(true);
    setTimeout(() => {
      window.print();
      setIsPrinting(false);
    }, 400);
  };

  // Table Columns
  const columns = [
    {
      name: "S.N",
      selector: (row, index) => index + 1,
      width: "75px",
    },
    {
      name: "Order Ref Id",
      selector: (row) => row.orderRefrenceId,
      cell: (row) => (
        <Link to={`/order/${row.orderRefrenceId}`}>{row.orderRefrenceId}</Link>
      ),
      sortable: true,
      // width: "20vw",
    },
    {
      name: "Order Date",
      selector: (row) => row.createdAt,
      cell: (row) => formatDateTimes(row.createdAt),
      sortable: true,
      // width: "20vw",
    },
    {
      name: "Table Name",
      selector: (row) => row.tableName,
      sortable: true,
    },
    {
      name: "Order Status",
      selector: (row) => row.orderStatus,
      cell: (row) => getStatusBadge(row.orderStatus),
      sortable: true,
    },
    {
      name: "Payment",
      selector: (row) => row.paymentStatus,
      // cell: (row) => <StatusBadge status={row.paymentStatus} type="payment" />,

      cell: (row) => getPaymentBadge(row.paymentStatus),
      sortable: true,
    },
    {
      name: "Discount",
      selector: (row) => row.discountAmount || 0,
      right: true,
    },
    {
      name: "Tax Amount",
      selector: (row) => row.taxAmount || 0,
      right: true,
    },
    {
      name: "Total Amt",
      selector: (row) => row.grandTotal,
      cell: (row) => <NumberWithCommas number={row.grandTotal} />,
      right: true,
    },
  ];
  const breadcrumbItems = [
    { title: "Back To List", onClick: () => navigate(-1) },
  ];

  return (
    <Container fluid>
      <div className="page-content">
        <BreadCrumb
          title="Day Close"
          pageTitle="Dashbaord"
          breadcrumbItems={breadcrumbItems}
        />

        {/* Subtle Day Status Card */}
        <Card className="shadow-sm border-0 mb-3">
          <CardBody className="py-3">
            <Row className="align-items-center">
              <Col md={6}>
                <div className="d-flex align-items-center gap-3">
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{
                      backgroundColor: isDayOpen ? "#d4edda" : "#f8d7da",
                      width: "48px",
                      height: "48px",
                    }}
                  >
                    <i
                      className={`bx ${isDayOpen ? "bx-sun" : "bx-moon"} fs-4`}
                      style={{ color: isDayOpen ? "#28a745" : "#dc3545" }}
                    />
                  </div>
                  <div>
                    <div className="d-flex align-items-center gap-2">
                      <h6 className="mb-0 fw-bold">
                        {isDayOpen ? "Day is Currently Open" : "Day is Closed"}
                      </h6>
                      <Badge
                        color={isDayOpen ? "success" : "danger"}
                        className="fs-6"
                        style={{ fontWeight: "500" }}
                      >
                        {isDayOpen ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                    <p className="text-muted mb-0 small mt-1">
                      <i className="bx bx-calendar me-1"></i>
                      {getCurrentDate()}
                    </p>
                  </div>
                </div>
              </Col>
              <Col md={6} className="text-md-end mt-3 mt-md-0">
                <div className="d-flex justify-content-md-end align-items-center gap-3">
                  <span className="text-muted small">Toggle Day Status</span>
                  <div className="form-check form-switch form-switch-lg mb-0">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={isDayOpen}
                      onChange={handleDayToggle}
                      disabled={isLoading}
                      style={{
                        cursor: "pointer",
                        width: "3rem",
                        height: "1.5rem",
                      }}
                    />
                  </div>
                  <Button
                    color="primary"
                    size="sm"
                    onClick={handlePrint}
                    className="d-flex align-items-center gap-2"
                  >
                    <i className="ri-printer-line"></i> Print
                  </Button>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Summary Cards */}
        <Row className="mt-2">
          <Col lg={3} md={6} sm={12}>
            <SummaryCard
              title="Total Orders"
              value={totalOrders}
              color="#0d6efd"
              icon="bx bx-shopping-bag"
            />
          </Col>
          <Col lg={3} md={6} sm={12}>
            <SummaryCard
              title="Total Sales Amount"
              value={totalSalesAmount}
              color="#28a745"
              icon="bx bx-check-circle"
            />
          </Col>
          <Col lg={3} md={6} sm={12}>
            <SummaryCard
              title="Actual Sales Amount"
              value={actualSalesAmount}
              color="#17a2b8"
              icon="bx bx-dollar-circle"
            />
          </Col>
          <Col lg={3} md={6} sm={12}>
            <SummaryCard
              title="Pending Payments"
              value={ongoingOrderValue}
              color="#ffc107"
              icon="bx bx-time-five"
            />
          </Col>
        </Row>

        {/* Order List */}
        <div
          className="p-3 p-lg-4 mt-1"
          style={{
            background: "#ffffff",
            borderRadius: "0.5rem",
            boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
            border: "1px solid #eee",
          }}
        >
          <div className="mb-3 d-flex justify-content-between align-items-center">
            <div>
              <h5 className="mb-1 fw-bold text-dark">Today's Orders</h5>
              <p className="text-muted small mb-0">
                Last updated: {new Date().toLocaleTimeString()}
              </p>
            </div>
          </div>
          <hr style={{ borderColor: "#dee2e6", marginTop: "0.5rem" }} />

          {isLoading ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <Triangle visible height="80" width="80" color="#5B71B9" />
              <h5 className="mt-1 ms-3">Loading...</h5>
            </div>
          ) : (
            <CustomDataTable
              responsive
              striped
              pagination
              fixedHeader
              highlightOnHover
              pointerOnHover
              persistTableHead
              progressPending={isLoading}
              customStyles={customStyles}
              progressComponent={
                <div className="my-3 text-center">
                  <Triangle visible height="80" width="80" color="#5B71B9" />
                  <h5 className="mt-1">Loading...</h5>
                </div>
              }
              columns={columns}
              data={summary}
              noDataComponent={
                <div className="text-center my-5 w-100">
                  <i className="ri-emotion-unhappy-line display-4 text-secondary" />
                  <h5 className="fw-semibold text-secondary">
                    No Orders Today
                  </h5>
                  <p className="text-muted mb-0">
                    There are no orders recorded for today yet.
                  </p>
                </div>
              }
            />
          )}
        </div>

        {/* Modals */}
        <ConfirmationDaybook
          isOpen={showCloseModal}
          toggle={toggleConfirm}
          orderData={orderData}
          performDayToggle={performDayToggle}
        />

        <CloseSummaryPrint
          isOpen={responseModal}
          toggle={toggleResponse}
          data={responseData}
        />
      </div>
    </Container>
  );
};

export default DaybookOrderSummary;
